<template>
  <div
    :aria-label="visible ? $t('gallery.lightbox_label') : null"
    :aria-modal="visible ? 'true' : null"
    :role="visible ? 'dialog' : null"
    class="pentagon-lightbox"
  >
    <mdb-lightbox
      ref="lightbox"
      :imgs="windowed"
      :index="index"
      :visible="visible"
      @hide="$emit('hide')"
    />
    <button
      v-if="visible"
      ref="close"
      :aria-label="$t('gallery.lightbox_close')"
      class="pentagon-lightbox__close"
      type="button"
      @click="close"
    >
      <mdb-icon icon="times"/>
    </button>
  </div>
</template>

<script>
import { mdbIcon, mdbLightbox } from 'mdbvue';

// A transparent pixel stands in for the photos that are not near the current one.
const BLANK = 'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';

/**
 * Thin wrapper around mdb-lightbox. The library renders every photo at once, which would load
 * every large variant of an album on the first click, hides its buttons on touch screens, and
 * moves no focus. The wrapper therefore hands it only the current, previous and next photo,
 * adds a close button that is always there, traps focus while open and returns it afterwards.
 * Escape, the arrow keys, the backdrop click and swiping stay the library's own.
 *
 * The root element moves to <body> when mounted: inside the routed page (a transformed element)
 * a fixed overlay is trapped under the navbar and the candles instead of covering them.
 */
export default {
  name: 'PhotoLightbox',
  components: {
    mdbIcon,
    mdbLightbox,
  },
  props: {
    // [{ src, caption }]
    photos: {
      type: Array,
      required: true,
    },
    index: {
      type: Number,
      default: 0,
    },
    visible: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      current: this.index,
      opener: null,
      bodyOverflow: '',
    };
  },
  computed: {
    windowed() {
      const count = this.photos.length;

      return this.photos.map((photo, position) => {
        const distance = Math.abs(position - this.current);
        const near = Math.min(distance, count - distance) <= 1;

        return { img: near ? photo.src : BLANK, caption: photo.caption };
      });
    },
  },
  watch: {
    visible(visible) {
      if (visible) {
        this.open();
      } else {
        this.release();
      }
    },
    index(index) {
      this.current = index;
    },
  },
  mounted() {
    document.body.appendChild(this.$el);

    // The library keeps its index to itself; follow it to know which photos to preload.
    this.stopFollowing = this.$watch(
      () => (this.$refs.lightbox ? this.$refs.lightbox.imgIndex : this.index),
      (position) => {
        this.current = position;
      },
    );
  },
  beforeDestroy() {
    this.stopFollowing();
    this.release();

    if (this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  methods: {
    open() {
      this.current = this.index;
      this.opener = document.activeElement;
      this.bodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.trapFocus);
      this.$nextTick(() => {
        if (this.$refs.close) {
          this.$refs.close.focus();
        }
      });
    },
    release() {
      document.removeEventListener('keydown', this.trapFocus);
      document.body.style.overflow = this.bodyOverflow;

      if (this.opener && this.opener.focus) {
        this.opener.focus();
      }

      this.opener = null;
    },
    close() {
      this.$refs.lightbox.closeDialog();
    },
    // Tab stays among the dialog's own buttons, in the order they appear.
    trapFocus(event) {
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = Array.from(this.$el.querySelectorAll('button:not([disabled])'))
        .filter(element => element.offsetParent !== null || element === document.activeElement);

      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!this.$el.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
  },
};
</script>
